import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';

import { MEDIA_QUERY_SMALL_SCREEN } from 'client/constants/mediaQueries';

import {
  formatPercentage,
} from 'common/formatters';

import {
  MARKETING_PARTNER_LISTING_SUNROOM_COMMISSION,
} from 'common/marketingPartnerListingConstants';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrPercentTicker from 'client/components/SrPercentTicker/SrPercentTicker';

const SR_COMMISSION_VALUE_MIN = MARKETING_PARTNER_LISTING_SUNROOM_COMMISSION;
const SR_COMMISSION_VALUE_MAX = 2;

const SrPercentTickerField = ({ value, onIncrease, onDecrease }) => {
  const tooltipRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const isSmallScreen = useMediaQuery({
    query: MEDIA_QUERY_SMALL_SCREEN,
  });

  useEffect(() => {
    return () => clearTimeout(tooltipRef.current);
  }, []);

  const _onIncrease = () => {
    clearTimeout(tooltipRef.current);
    setTooltip(null);
    if (value < 2) {
      onIncrease();
    }
    else {
      setTooltip({
        direction: 'right',
        text: 'Sorry, you can’t set it higher. Contact your account manager to submit ' +
          'this property with a higher commission.',
      });
      tooltipRef.current = setTimeout(() => {
        setTooltip(null);
      }, 5000);
    }
  };

  const _onDecrease = () => {
    clearTimeout(tooltipRef.current);
    setTooltip(null);
    if (value > 0.85) {
      onDecrease();
    }
    else {
      setTooltip({
        direction: 'left',
        text: 'Sorry, you can’t set it lower. Contact your account manager to submit ' +
          'this property with a lower commission.',
      });
      tooltipRef.current = setTimeout(() => {
        setTooltip(null);
      }, 5000);
    }
  };

  return (
    <SrBox
      borderRadius={isSmallScreen ? '14px' : '24px'}
      backgroundColor='white'
      boxShadow='0px 5px 21px rgba(25, 51, 64, 0.05)'
      padding='23px 28px 30px'
    >
      <SrBox
        maxWidth='375px'
        margin='0 auto'
      >
        <SrPercentTicker
          currentPercent={value}
          onIncrease={_onIncrease}
          onDecrease={_onDecrease}
          header='Client Pays'
          tooltip={tooltip}
          min={SR_COMMISSION_VALUE_MIN}
          max={SR_COMMISSION_VALUE_MAX}
        />
        <SrBox marginTop='22px'>
          <SrFlex justify='around'>
            <SrBox>
              <SrText
                color='blueGreycliff'
                font='greycliff'
                size='16'
                lineHeight='30px'
                weight='800'
                display='block'
                align='center'
                opacity='0.5'
              >
                You Earn
              </SrText>
              <SrText
                color='green'
                font='greycliff'
                size='21'
                lineHeight='30px'
                weight='800'
                display='block'
                align='center'
              >
                {formatPercentage(value - MARKETING_PARTNER_LISTING_SUNROOM_COMMISSION)}
              </SrText>
            </SrBox>
            <SrBox>
              <SrText
                color='blueGreycliff'
                font='greycliff'
                size='16'
                lineHeight='30px'
                weight='800'
                display='block'
                align='center'
                opacity='0.5'
              >
                Sunroom Earns
              </SrText>
              <SrText
                color='green'
                font='greycliff'
                size='21'
                lineHeight='30px'
                weight='800'
                display='block'
                align='center'
              >
                {formatPercentage(MARKETING_PARTNER_LISTING_SUNROOM_COMMISSION)}
              </SrText>
            </SrBox>
          </SrFlex>
        </SrBox>
      </SrBox>
    </SrBox>
  );
};

SrPercentTickerField.propTypes = {
  value: PropTypes.number.isRequired,
  onIncrease: PropTypes.func.isRequired,
  onDecrease: PropTypes.func.isRequired,
};

SrPercentTickerField.displayName = 'SrPercentTickerField';

export default SrPercentTickerField;
